import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n/LanguageContext';
import { supabase } from '@/lib/supabase';

interface CostsWidgetProps {
  carId: string;
}

const CostsWidget: React.FC<CostsWidgetProps> = ({ carId }) => {
  const { formatCurrency, t } = useI18n();
  const navigate = useNavigate();
  const [monthlyAvg, setMonthlyAvg] = useState<number | null>(null);
  const [serviceCostsThisYear, setServiceCostsThisYear] = useState<number>(0);
  const [fuelCostsMonthlyAvg, setFuelCostsMonthlyAvg] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!carId) {
      setMonthlyAvg(null);
      setServiceCostsThisYear(0);
      setFuelCostsMonthlyAvg(null);
      setLoading(false);
      return;
    }

    const loadCosts = async () => {
      try {
        setLoading(true);
        const currentYear = new Date().getFullYear();
        
        // 1. Calculate monthly average for all categories (expenses + service entries)
        // Get ALL expenses (not filtered by year) to calculate historical monthly average
        const { data: expensesData, error: expensesError } = await supabase
          .from('expenses')
          .select('expense_date, amount_cents')
          .eq('car_id', carId)
          .is('deleted_at', null)
          .not('amount_cents', 'is', null);

        if (expensesError) {
          console.error('Failed to load expenses:', expensesError);
        }

        // Get ALL service entries (not filtered by year) to calculate historical monthly average
        const { data: serviceData, error: serviceError } = await supabase
          .from('service_entries')
          .select('service_date, total_cents')
          .eq('car_id', carId)
          .not('total_cents', 'is', null);

        if (serviceError) {
          console.error('Failed to load service entries:', serviceError);
        }

        // Calculate monthly totals across ALL years using YYYY-MM as key
        const monthlyTotals: Record<string, number> = {};
        
        // Process expenses - group by year-month
        (expensesData || []).forEach((row: any) => {
          const expenseDate = new Date(row.expense_date);
          const yearMonth = `${expenseDate.getFullYear()}-${String(expenseDate.getMonth() + 1).padStart(2, '0')}`;
          const amount = (row.amount_cents ?? 0) / 100;
          monthlyTotals[yearMonth] = (monthlyTotals[yearMonth] || 0) + amount;
        });

        // Process service entries - group by year-month
        (serviceData || []).forEach((row: any) => {
          const serviceDate = new Date(row.service_date);
          const yearMonth = `${serviceDate.getFullYear()}-${String(serviceDate.getMonth() + 1).padStart(2, '0')}`;
          const amount = (row.total_cents ?? 0) / 100;
          monthlyTotals[yearMonth] = (monthlyTotals[yearMonth] || 0) + amount;
        });

        // Calculate average across all months with data (all historical data)
        const monthsWithData = Object.values(monthlyTotals);
        if (monthsWithData.length > 0) {
          const total = monthsWithData.reduce((sum, amount) => sum + amount, 0);
          setMonthlyAvg(total / monthsWithData.length);
        } else {
          setMonthlyAvg(null);
        }

        // 2. Calculate service costs this year
        let thisYearServiceTotal = 0;
        (serviceData || []).forEach((row: any) => {
          const serviceDate = new Date(row.service_date);
          const serviceYear = serviceDate.getFullYear();
          
          if (serviceYear === currentYear) {
            thisYearServiceTotal += (row.total_cents ?? 0) / 100;
          }
        });
        setServiceCostsThisYear(thisYearServiceTotal);

        // 3. Calculate fuel costs monthly average across ALL historical data
        const { data: fuelData, error: fuelError } = await supabase
          .from('expenses')
          .select('expense_date, amount_cents')
          .eq('car_id', carId)
          .eq('category', 'Refueling')
          .is('deleted_at', null)
          .not('amount_cents', 'is', null);

        if (fuelError) {
          console.error('Failed to load fuel expenses:', fuelError);
        }

        // Calculate monthly fuel totals across ALL years using YYYY-MM as key
        const monthlyFuelTotals: Record<string, number> = {};
        (fuelData || []).forEach((row: any) => {
          const expenseDate = new Date(row.expense_date);
          const yearMonth = `${expenseDate.getFullYear()}-${String(expenseDate.getMonth() + 1).padStart(2, '0')}`;
          const amount = (row.amount_cents ?? 0) / 100;
          monthlyFuelTotals[yearMonth] = (monthlyFuelTotals[yearMonth] || 0) + amount;
        });

        // Calculate fuel monthly average across all months with data (all historical data)
        const fuelMonthsWithData = Object.values(monthlyFuelTotals);
        if (fuelMonthsWithData.length > 0) {
          const fuelTotal = fuelMonthsWithData.reduce((sum, amount) => sum + amount, 0);
          setFuelCostsMonthlyAvg(fuelTotal / fuelMonthsWithData.length);
        } else {
          setFuelCostsMonthlyAvg(null);
        }
      } catch (error) {
        console.error('Error loading costs:', error);
        setMonthlyAvg(null);
        setServiceCostsThisYear(0);
        setFuelCostsMonthlyAvg(null);
      } finally {
        setLoading(false);
      }
    };

    loadCosts();
  }, [carId]);

  return (
    <div 
      className="p-3 bg-white rounded-lg border border-gray-200" 
      style={{
        boxShadow: '0px 4px 4px -1px #0C0C0D0D'
      }}
    >
      {/* Title Section */}
      <div className="flex items-center justify-between mb-3">
        <h3 
          className="text-xs font-semibold text-black"
          style={{
            fontFamily: 'Lato',
            fontWeight: 700,
            fontSize: '12px',
            lineHeight: '16px',
            letterSpacing: '0.3px'
          }}
        >
          {t('costs.title') || 'Costs'}
        </h3>
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/cars/${carId}?subTab=costs`);
          }}
          className="flex items-center justify-center"
          aria-label="View all expenses"
        >
          <img 
            src="/icons/Component 3.png" 
            alt="Navigate to costs" 
            className="w-5 h-5"
          />
        </button>
      </div>

      {/* Metrics Section - Labels Row */}
      <div className="flex items-start gap-4 sm:gap-6 mb-2">
        {/* Monthly avg label */}
        <div className="flex-1 min-w-0">
          <p 
            className="text-xs text-gray-500 whitespace-nowrap"
            style={{
              fontFamily: 'Lato',
              fontWeight: 400,
              fontSize: '11px',
              lineHeight: '16px'
            }}
          >
            {t('costs.monthlyAvg') || 'Monthly avg'}
          </p>
        </div>

        {/* Divider */}
        <div className="h-4 w-px bg-gray-200 flex-shrink-0 mt-0.5" />

        {/* Service costs this year label */}
        <div className="flex-1 min-w-0">
          <p 
            className="text-xs text-gray-500 leading-tight"
            style={{
              fontFamily: 'Lato',
              fontWeight: 400,
              fontSize: '11px',
              lineHeight: '16px',
              maxWidth: '100%'
            }}
          >
            {t('costs.serviceCostsThisYear') || 'Service costs this year'}
          </p>
        </div>

        {/* Divider */}
        <div className="h-4 w-px bg-gray-200 flex-shrink-0 mt-0.5" />

        {/* Fuel costs monthly avg label */}
        <div className="flex-1 min-w-0">
          <p 
            className="text-xs text-gray-500 leading-tight"
            style={{
              fontFamily: 'Lato',
              fontWeight: 400,
              fontSize: '11px',
              lineHeight: '16px',
              maxWidth: '100%'
            }}
          >
            {t('costs.fuelCostsMonthlyAvg') || 'Fuel costs monthly avg'}
          </p>
        </div>
      </div>

      {/* Values Row - All on same line */}
      <div className="flex items-center gap-4 sm:gap-6">
        {/* Monthly avg value */}
        <div className="flex-1 min-w-0">
          <p 
            className="text-sm font-bold text-black whitespace-nowrap"
            style={{
              fontFamily: 'Lato',
              fontWeight: 700,
              fontSize: '14px',
              lineHeight: '20px'
            }}
          >
            {loading ? '...' : (monthlyAvg !== null ? formatCurrency(monthlyAvg) : '-')}
          </p>
        </div>

        {/* Divider */}
        <div className="h-5 w-px bg-gray-200 flex-shrink-0" />

        {/* Service costs this year value */}
        <div className="flex-1 min-w-0">
          <p 
            className="text-sm font-bold text-black whitespace-nowrap"
            style={{
              fontFamily: 'Lato',
              fontWeight: 700,
              fontSize: '14px',
              lineHeight: '20px'
            }}
          >
            {loading ? '...' : formatCurrency(serviceCostsThisYear)}
          </p>
        </div>

        {/* Divider */}
        <div className="h-5 w-px bg-gray-200 flex-shrink-0" />

        {/* Fuel costs monthly avg value */}
        <div className="flex-1 min-w-0">
          <p 
            className="text-sm font-bold text-black whitespace-nowrap"
            style={{
              fontFamily: 'Lato',
              fontWeight: 700,
              fontSize: '14px',
              lineHeight: '20px'
            }}
          >
            {loading ? '...' : (fuelCostsMonthlyAvg !== null ? formatCurrency(fuelCostsMonthlyAvg) : '-')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CostsWidget;

